/** Meta Cloud API media size limits (bytes) — keep in sync with backend media_limits.py */
export const MAX_FILE_SIZES_BYTES: Record<string, number> = {
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  audio: 16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

const MIME_TO_TYPE: Record<string, string> = {
  'image/jpeg': 'image',
  'image/jpg': 'image',
  'image/png': 'image',
  'video/mp4': 'video',
  'video/3gpp': 'video',
  'audio/ogg': 'audio',
  'audio/mpeg': 'audio',
  'audio/mp4': 'audio',
  'audio/aac': 'audio',
  'audio/amr': 'audio',
  'audio/webm': 'audio',
  'audio/wav': 'audio',
  'audio/x-wav': 'audio',
  'application/pdf': 'document',
  'text/plain': 'document',
  'application/msword': 'document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
  'application/vnd.ms-excel': 'document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'document',
  'application/vnd.ms-powerpoint': 'document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'document',
};

export const INBOX_ACCEPT =
  'image/jpeg,image/png,video/mp4,video/3gpp,audio/ogg,audio/mpeg,audio/mp4,audio/aac,audio/amr,audio/webm,audio/wav,application/pdf,text/plain,.doc,.docx,.xls,.xlsx,.ppt,.pptx';

export function resolveOutboundMediaType(mime: string): string | null {
  const normalized = (mime || '').split(';')[0].trim().toLowerCase();
  return MIME_TO_TYPE[normalized] || null;
}

export function validateOutboundFile(file: File): { ok: true; mediaType: string } | { ok: false; error: string } {
  const mediaType = resolveOutboundMediaType(file.type);
  if (!mediaType) {
    return { ok: false, error: `Tipo no soportado: ${file.type || file.name}` };
  }
  const max = MAX_FILE_SIZES_BYTES[mediaType];
  if (file.size > max) {
    const maxMb = max / (1024 * 1024);
    const actualMb = file.size / (1024 * 1024);
    return {
      ok: false,
      error: `Archivo demasiado grande (${actualMb.toFixed(1)}MB). Máximo para ${mediaType}: ${maxMb}MB`,
    };
  }
  return { ok: true, mediaType };
}

/** Map axios/network/API upload failures to user-facing Spanish messages. */
export function mapMediaUploadError(err: any): string {
  const status = err?.response?.status as number | undefined;
  const detail = err?.response?.data?.detail;
  const code = err?.code as string | undefined;
  const message = String(err?.message || '');

  if (status === 413) {
    return 'El archivo es demasiado grande para el servidor. Máximo permitido: video/audio 16MB, imagen 5MB, documento 100MB.';
  }
  if (status === 400 && detail) {
    if (typeof detail === 'string' && detail.toLowerCase().includes('file too large')) {
      return 'El archivo supera el tamaño máximo permitido por WhatsApp.';
    }
    return typeof detail === 'string' ? detail : 'No se pudo enviar el archivo.';
  }
  if (status === 401 || status === 403) {
    return 'No tienes permisos para enviar archivos. Vuelve a iniciar sesión.';
  }
  if (status === 405) {
    return 'El servidor no acepta este tipo de envío. Contacta a soporte si el problema continúa.';
  }
  if (code === 'ECONNABORTED' || message.toLowerCase().includes('timeout')) {
    return 'La subida tardó demasiado. Intenta con un archivo más liviano o vuelve a intentar.';
  }
  if (
    !err?.response ||
    code === 'ERR_NETWORK' ||
    message === 'Network Error' ||
    message.toLowerCase().includes('network error')
  ) {
    return 'No se pudo conectar con el servidor al subir el archivo. Revisa tu conexión e inténtalo de nuevo.';
  }
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return 'No se pudo enviar el archivo. Inténtalo de nuevo.';
}
